import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, TOKENS } from '../context/ThemeContext';
import { useUserTier } from '../context/UserTierContext';
import { supabase } from '../supabase';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const PATHS = [
  {
    icon: '⚡',
    title: 'Challenges',
    desc: 'Apex, SOQL, LWC. Write real code, get instant feedback.',
    cta: 'Start coding →',
    path: '/challenges/apex',
    primary: true,
  },
  {
    icon: '🃏',
    title: 'Flashcards',
    desc: 'Spaced repetition for Salesforce concepts. 5 min a day.',
    cta: 'Study now →',
    path: '/flashcards',
  },
  {
    icon: '🔥',
    title: 'Apex30',
    desc: '30 days of daily Apex. Build the habit, earn the streak.',
    cta: 'Join challenge →',
    path: '/apex30',
  },
  {
    icon: '🎯',
    title: 'Interview Prep',
    desc: 'Real Salesforce interview questions by role.',
    cta: 'Prepare now →',
    path: '/interview',
  },
  {
    icon: '📊',
    title: 'My Progress',
    desc: 'Topic breakdown, difficulty stats, recent activity and streak.',
    cta: 'View progress →',
    path: '/profile',
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { tier } = useUserTier();
  const navigate = useNavigate();
  const c = TOKENS[theme];

  const [stats, setStats]           = useState({ solved: 0, points: 0 });
  const [flashStats, setFlashStats] = useState({ due: 0, mastered: 0 });
  const [loading, setLoading]       = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const firstName = user?.user_metadata?.full_name?.split(' ')[0]
    || user?.name?.split(' ')[0]
    || 'there';

  const hourOfDay = new Date().getHours();
  const greeting = hourOfDay < 12 ? 'Good morning' : hourOfDay < 18 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    if (!user) return;
    async function load() {
      try {
        const [subRes, flashRes] = await Promise.all([
          fetch(`${API}/submissions/${user.id}`),
          fetch(`${API}/flashcard-stats/${user.id}`),
        ]);
        const subData   = await subRes.json();
        const flashData = await flashRes.json();
        setStats({ solved: subData.solvedIds?.length || 0, points: (subData.solvedIds?.length || 0) * 10 });
        setFlashStats({ due: flashData.due || 0, mastered: flashData.mastered || 0 });
      } catch (e) {
        // fail silently
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user]);

  const tierLabel = tier === 'admin' ? '👑 Admin' : tier === 'pro' ? '⚡ Pro' : tier === 'team' ? '👥 Team' : '🆓 Free';
  const NAV_LINKS = [
    ['Challenges', '/challenges/apex'],
    ['My Progress', '/profile'],
    ['Flashcards', '/flashcards'],
    ['Pricing', '/pricing'],
  ];

  return (
    <>
      <SEO title="Dashboard — TrailCode" description="Your TrailCode dashboard." />
      <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>

        {/* ── Nav ── */}
        <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 clamp(24px, 6vw, 80px)', height: 58, background: c.navBg, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px', cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
            Trail<span style={{ color: c.accent }}>Code</span>
          </div>

          {/* Nav links */}
          <div style={{ display: 'flex', gap: 4 }}>
            {NAV_LINKS.map(([label, path]) => (
              <span key={label} onClick={() => navigate(path)}
                style={{ color: c.secondary, fontSize: 13, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', transition: 'color 0.15s, background 0.15s' }}
                onMouseEnter={e => { e.target.style.color = c.text; e.target.style.background = c.card; }}
                onMouseLeave={e => { e.target.style.color = c.secondary; e.target.style.background = 'transparent'; }}>
                {label}
              </span>
            ))}
          </div>

          {/* User controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {user?.user_metadata?.avatar_url && (
              <img src={user.user_metadata.avatar_url} alt="avatar" style={{ width: 28, height: 28, borderRadius: '50%', border: `1.5px solid ${c.border}`, cursor: 'pointer' }} onClick={() => navigate('/profile')} />
            )}
            <span style={{ fontSize: 13, color: c.secondary }}>{firstName}</span>
            <span style={{ fontSize: 11, background: c.accentBg, color: c.accent, border: `1px solid ${c.accentBorder}`, borderRadius: 99, padding: '2px 10px', fontWeight: 600 }}>{tierLabel}</span>
            {tier === 'admin' && (
              <button onClick={() => navigate('/admin')} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: "'Inter', sans-serif" }}>Admin</button>
            )}
            <button
              onClick={async () => { setIsDisconnecting(true); await supabase.auth.signOut(); navigate('/'); }}
              style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: "'Inter', sans-serif" }}>
              {isDisconnecting ? '⏳' : 'Logout'}
            </button>
          </div>
        </nav>

        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px clamp(24px, 6vw, 80px)' }}>

          {/* ── Greeting ── */}
          <div style={{ marginBottom: 40 }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: c.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
              {greeting}, {firstName} 👋
            </h1>
            <p style={{ fontSize: 14, color: c.secondary, margin: 0 }}>
              What are you working on today?
            </p>
          </div>

          {/* ── Stats row ── */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'Challenges solved', value: loading ? '—' : stats.solved },
              { label: 'Points earned',     value: loading ? '—' : stats.points },
              { label: 'Cards mastered',    value: loading ? '—' : flashStats.mastered },
              { label: 'Cards due today',   value: loading ? '—' : flashStats.due, highlight: flashStats.due > 0 },
            ].map((s, i) => (
              <div key={i} style={{ background: c.card, border: `1px solid ${s.highlight ? c.accent : c.border}`, borderRadius: 12, padding: '18px 24px', flex: '1 1 160px' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: s.highlight ? c.accent : c.text, letterSpacing: '-1px' }}>{s.value}</div>
                <div style={{ fontSize: 12, color: c.secondary, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Progress link ── */}
          <div
            onClick={() => navigate('/profile')}
            style={{ marginBottom: 32, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: c.accent }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            View full progress breakdown →
          </div>

          {/* ── Flashcards due nudge ── */}
          {!loading && flashStats.due > 0 && (
            <div
              onClick={() => navigate('/flashcards')}
              style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}`, borderRadius: 12, padding: '14px 20px', marginBottom: 24, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: c.accent, fontWeight: 600 }}>
                🃏 You have {flashStats.due} flashcard{flashStats.due > 1 ? 's' : ''} due for review
              </span>
              <span style={{ fontSize: 13, color: c.accent }}>Review now →</span>
            </div>
          )}

          {/* ── Upgrade nudge for free users ── */}
          {tier === 'free' && (
            <div
              onClick={() => navigate('/pricing')}
              style={{ background: '#08111f', border: '1px solid #1c2d45', borderRadius: 12, padding: '14px 20px', marginBottom: 32, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#4f8ef7', fontWeight: 600 }}>
                ⚡ Upgrade to Pro — unlock all challenges, solutions, Apex30 & more
              </span>
              <span style={{ fontSize: 13, color: '#4f8ef7' }}>See plans →</span>
            </div>
          )}

          {/* ── Path cards ── */}
          <h2 style={{ fontSize: 18, fontWeight: 700, color: c.text, margin: '0 0 20px', letterSpacing: '-0.3px' }}>Pick your path</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {PATHS.map((p) => (
              <div
                key={p.path}
                onClick={() => navigate(p.path)}
                style={{
                  background: p.primary ? c.accent : c.card,
                  border: `1px solid ${p.primary ? c.accent : c.border}`,
                  borderRadius: 14,
                  padding: '24px 20px',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = theme === 'dark' ? '0 8px 24px rgba(79,142,247,0.15)' : '0 8px 24px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <span style={{ fontSize: 26 }}>{p.icon}</span>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: p.primary ? (theme === 'dark' ? '#08090e' : '#fff') : c.text }}>{p.title}</h3>
                <p style={{ fontSize: 13, color: p.primary ? (theme === 'dark' ? '#08090ecc' : '#ffffffcc') : c.secondary, lineHeight: 1.6, margin: 0, flexGrow: 1 }}>{p.desc}</p>
                <span style={{ fontSize: 13, fontWeight: 600, color: p.primary ? (theme === 'dark' ? '#08090e' : '#fff') : c.accent }}>{p.cta}</span>
              </div>
            ))}
          </div>

        </div>
        <style>{`* { box-sizing: border-box; }`}</style>
      </div>
    </>
  );
}